/**
 * "Cached data" section for the Profile screen.
 *
 * Surfaces the app-owned (partner-namespaced) localStorage that the portal
 * quietly persists — reference-data "selections" (document groups/types, loan
 * field options), saved pipeline layouts/filters, table column state, and UI
 * preferences — and lets the user inspect, refresh, or delete each entry (or
 * clear them all at once). Only keys that flow through nsKey()/partnerStorage
 * are listed; third-party storage (Stytch session, etc.) is never namespaced,
 * so it never appears here and a bulk clear can't sign the user out.
 *
 * "Refresh" is only meaningful for the reference-data entries (they can be
 * refetched from the backend); for everything else the action is delete only.
 */
import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import CodeOutlined from "@mui/icons-material/CodeOutlined";
import DeleteSweepOutlined from "@mui/icons-material/DeleteSweepOutlined";
import OpenInFullOutlined from "@mui/icons-material/OpenInFullOutlined";
import CloseFullscreenOutlined from "@mui/icons-material/CloseFullscreenOutlined";
import { useSnackbar } from "notistack";

import { T } from "../SupportScreen/components/supportUi";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import {
  listPartnerStorageEntries,
  clearAllPartnerStorage,
} from "../../utils/storage/partnerStorage";
import { useFetchedSelectionsRequestsHook } from "../../hooks/useFetchedSelectionsRequestsHook";
import JsonPreview from "../../components/common/JsonPreview";
import SearchInput from "../../components/inputs/SearchInput";

// ── Key → human metadata ─────────────────────────────────────────────────────
const CATEGORY = {
  reference: "Reference data (selections)",
  pipeline: "Pipeline views & filters",
  tables: "Table layouts",
  prefs: "Preferences",
  session: "Session & sign-in",
  other: "Other",
};
// Render order for the category groups.
const CATEGORY_ORDER = [
  "reference",
  "pipeline",
  "tables",
  "prefs",
  "session",
  "other",
];

const prettifyKey = (key) =>
  String(key)
    .replace(/^app\./, "")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());

// Human-readable metadata for each known key: a plain-English title plus a
// one-sentence description of what the saved value is actually for. Anything
// not listed here falls back to a prettified key + a generic description.
// Shape: key -> [title, category, description].
const EXACT = {
  "lo-pipeline-layouts": [
    "Saved pipeline views",
    "pipeline",
    "The custom column arrangements and views you've saved for the loan pipeline.",
  ],
  "lo-pipeline-default-layout": [
    "Default pipeline view",
    "pipeline",
    "Which of your saved views the pipeline opens to automatically.",
  ],
  "lo-pipeline-assigned-to-me": [
    "“Assigned to me” pipeline filter",
    "pipeline",
    "Remembers whether the pipeline is limited to loans assigned to you.",
  ],
  "lo-pipeline-favorite-loans": [
    "Favorited loans",
    "pipeline",
    "The loans you've starred as favorites in the pipeline.",
  ],
  "lo-pipeline-show-favorites": [
    "“Favorites only” pipeline filter",
    "pipeline",
    "Remembers whether the pipeline is showing only your favorited loans.",
  ],
  "lo-pipeline-remember-session": [
    "Reopen my last pipeline view",
    "pipeline",
    "Whether the pipeline reopens with the same filters and view you last used.",
  ],
  "lo-pipeline-last-session": [
    "Last pipeline view",
    "pipeline",
    "A snapshot of the filters and view from the last time you used the pipeline.",
  ],
  "lo-pipeline-compact": [
    "Compact row height",
    "pipeline",
    "Whether the pipeline table is using the denser, compact row height.",
  ],
  "lo-pipeline-compact-hint-dismissed": [
    "Compact-view tip dismissed",
    "pipeline",
    "Remembers that you've dismissed the tip about compact row height.",
  ],
  "app.loanPipelineTable.savedColumnDefs": [
    "Pipeline table columns",
    "tables",
    "Which columns are shown, and their order and widths, in the pipeline table.",
  ],
  "app.loanPipelineTable.defaultTableSort": [
    "Pipeline default sort",
    "tables",
    "The column and direction the pipeline table sorts by by default.",
  ],
  "app.loanPipelineTable.defaultTabletatusFilter": [
    "Pipeline status filter",
    "pipeline",
    "The loan-status filter you last applied to the pipeline table.",
  ],
  "app.loanPipelineTable.defaultTablePipelineFilter": [
    "Pipeline stage filter",
    "pipeline",
    "The pipeline-stage filter you last applied to the pipeline table.",
  ],
  "app.loanPipelineTable.defaultTableSalespersonFilter": [
    "Pipeline salesperson filter",
    "pipeline",
    "The salesperson filter you last applied to the pipeline table.",
  ],
  "app.loanPipelineTable.defaultTablePipelineFilterCollapsed": [
    "Pipeline filter panel state",
    "pipeline",
    "Whether the pipeline's filter panel is collapsed or expanded.",
  ],
  "app.loanPipelineTable.filterByUserSalesperson": [
    "“My loans” pipeline filter",
    "pipeline",
    "Remembers whether the pipeline is limited to loans where you're the salesperson.",
  ],
  "app.loanPipelineTable.showTooltipHover": [
    "Pipeline hover tooltips",
    "prefs",
    "Whether loan detail tooltips appear when you hover over pipeline rows.",
  ],
  loanConditionsTableColumnState: [
    "Conditions table columns",
    "tables",
    "The column layout, order, and widths for the loan Conditions table.",
  ],
  "app.loanDrilldown.showTreeMap": [
    "Loan tree-map view",
    "prefs",
    "Whether the loan drilldown shows the tree-map visualization.",
  ],
  loanTooltipCustomFields: [
    "Loan tooltip fields",
    "prefs",
    "The loan fields you chose to display in hover tooltips.",
  ],
  sidebar_open: [
    "Navigation sidebar",
    "prefs",
    "Whether the left navigation sidebar is expanded or collapsed.",
  ],
  "auth.hasSignedInBefore": [
    "Returning-user flag",
    "session",
    "Remembers that you've signed in on this device before, so first-time setup prompts stay hidden.",
  ],
  "login.savedEmail": [
    "Saved sign-in email",
    "session",
    "The email address pre-filled for you on the sign-in screen.",
  ],
  "login.rememberMe": [
    "“Remember me” at sign-in",
    "session",
    "Whether the sign-in screen keeps you signed in on this device.",
  ],
  "login.preferredTab": [
    "Preferred sign-in method",
    "session",
    "Whether the sign-in screen defaults to the email-code or password option.",
  ],
  "login.pendingCode": [
    "In-progress sign-in code",
    "session",
    "A temporary record of an email sign-in code you're in the middle of entering.",
  ],
  "support-ticket-hint-dismissed": [
    "Support tip dismissed",
    "session",
    "Remembers that you've dismissed the tip about submitting a support ticket.",
  ],
};

// Resolve a raw (de-namespaced) key to { label, description, category, refresh }.
// `refresh` is a { kind, objectType? } descriptor when the entry can be
// refetched from the backend, otherwise null.
const describeKey = (key) => {
  if (key === "fetchedSelections.fileDocGroupAndTypes")
    return {
      label: "Document categories & file types",
      description:
        "The list of document groups and file types used when uploading and classifying loan documents.",
      category: "reference",
      refresh: { kind: "docTypes" },
    };
  if (key.startsWith("fetchedSelections.modelSchema.")) {
    const objectType = key.slice("fetchedSelections.modelSchema.".length);
    return {
      label: "Loan field dropdown options",
      description: `The dropdown choices for ${objectType} fields (status, product type, purpose, and more) used throughout the app's forms.`,
      category: "reference",
      refresh: { kind: "modelSchema", objectType },
    };
  }
  if (key.startsWith("fetchedSelections."))
    return {
      label: prettifyKey(key),
      description: "Reference data saved from the server to speed up page loads.",
      category: "reference",
      refresh: null,
    };

  if (EXACT[key])
    return {
      label: EXACT[key][0],
      category: EXACT[key][1],
      description: EXACT[key][2],
      refresh: null,
    };

  if (key.startsWith("app.loanPipelineTable."))
    return {
      label: prettifyKey(key),
      description: "A saved filter, sort, or column setting for the pipeline table.",
      category: "pipeline",
      refresh: null,
    };
  if (key.startsWith("lo-workspace-tabs"))
    return {
      label: "Pinned workspace tabs",
      description: "The loan tabs you've pinned open across the top of the app.",
      category: "prefs",
      refresh: null,
    };
  if (key.startsWith("lo-pipeline-"))
    return {
      label: prettifyKey(key),
      description: "A saved pipeline preference or filter.",
      category: "pipeline",
      refresh: null,
    };
  if (key.startsWith("login."))
    return {
      label: prettifyKey(key),
      description: "A saved sign-in preference.",
      category: "session",
      refresh: null,
    };

  return {
    label: prettifyKey(key),
    description: "App data saved in your browser.",
    category: "other",
    refresh: null,
  };
};

// ── Value inspection ─────────────────────────────────────────────────────────
const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Parse the value for a friendly one-liner + an "updated" timestamp when the
// entry is a TTL selection-cache wrapper ({ data, savedAt }).
const summarizeValue = (value) => {
  let parsed;
  try {
    parsed = JSON.parse(value);
  } catch {
    const s = String(value);
    return { summary: s.length > 40 ? `${s.slice(0, 40)}…` : s, savedAt: null };
  }
  let savedAt = null;
  let data = parsed;
  if (parsed && typeof parsed === "object" && "savedAt" in parsed && "data" in parsed) {
    savedAt = parsed.savedAt;
    data = parsed.data;
  }
  let summary;
  if (Array.isArray(data)) summary = `${data.length} item${data.length === 1 ? "" : "s"}`;
  else if (data && typeof data === "object")
    summary = `${Object.keys(data).length} field${Object.keys(data).length === 1 ? "" : "s"}`;
  else summary = String(data);
  return { summary, savedAt };
};

// Parse the stored string into a JS value for JsonPreview; fall back to the raw
// string when it isn't JSON.
const parseValue = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const isJsonTree = (v) => v !== null && typeof v === "object";

// ── Row ──────────────────────────────────────────────────────────────────────
const CacheRow = ({ entry, busy, onRefresh, onDelete }) => {
  const [open, setOpen] = useState(false);
  // Height toggle for the JSON viewer: compact by default, ~full screen when
  // expanded.
  const [tall, setTall] = useState(false);
  const { summary, savedAt } = useMemo(
    () => summarizeValue(entry.value),
    [entry.value],
  );
  const parsed = useMemo(() => parseValue(entry.value), [entry.value]);
  const { label, description, refresh } = entry.meta;

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${T.line}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1.25 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ fontSize: 13.5, fontWeight: 600, color: T.ink900 }}>{label}</Box>
          <Box sx={{ fontSize: 12, color: T.ink500, mt: "2px", lineHeight: 1.45 }}>
            {description}
          </Box>
        </Box>
        <Box sx={{ textAlign: "right", flexShrink: 0, mr: 0.5 }}>
          <Box sx={{ fontSize: 12.5, color: T.ink700 }}>{summary}</Box>
          <Box sx={{ fontSize: 11, color: T.ink300 }}>
            {formatBytes(entry.sizeBytes)}
            {savedAt ? ` · updated ${formatDateTime(new Date(savedAt).toISOString(), "date")}` : ""}
          </Box>
        </Box>
        <Tooltip title="View stored value">
          <IconButton size="small" onClick={() => setOpen((o) => !o)} sx={{ color: T.ink500 }}>
            <CodeOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        {refresh && (
          <Tooltip title="Refresh from server">
            <span>
              <IconButton
                size="small"
                onClick={() => onRefresh(entry)}
                disabled={busy}
                sx={{ color: T.teal600 }}
              >
                {busy ? <CircularProgress size={16} /> : <RefreshOutlined fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => onDelete(entry)} sx={{ color: T.red }}>
            <DeleteOutlineOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: "6px",
          }}
        >
          <Box
            sx={{
              fontSize: 10.5,
              color: T.ink300,
              flex: 1,
              minWidth: 0,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              wordBreak: "break-all",
            }}
          >
            {entry.key}
          </Box>
          <Button
            size="small"
            variant="text"
            startIcon={
              tall ? (
                <CloseFullscreenOutlined sx={{ fontSize: 15 }} />
              ) : (
                <OpenInFullOutlined sx={{ fontSize: 15 }} />
              )
            }
            onClick={() => setTall((v) => !v)}
            sx={{ flexShrink: 0, fontSize: 11.5, fontWeight: 600 }}
          >
            {tall ? "Collapse height" : "Expand height"}
          </Button>
        </Box>
        <Box
          sx={{
            m: "0 0 12px",
            p: "10px 12px",
            background: "#fff",
            border: `1px solid ${T.line}`,
            borderRadius: "8px",
            maxHeight: tall ? "calc(100vh - 180px)" : 260,
            overflow: "auto",
            transition: "max-height .15s ease",
          }}
        >
          {isJsonTree(parsed) ? (
            <JsonPreview values={parsed} defaultCollapse={4} />
          ) : (
            <Box
              component="pre"
              sx={{
                m: 0,
                fontSize: 11.5,
                lineHeight: 1.55,
                color: T.ink700,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {String(parsed)}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Card ─────────────────────────────────────────────────────────────────────
export default function ProfileCachedDataCard() {
  const { enqueueSnackbar } = useSnackbar();
  const { getLoanFileDocGroupsAndTypes, getModelSelectOptions } =
    useFetchedSelectionsRequestsHook();

  const [tick, setTick] = useState(0);
  const [busyKey, setBusyKey] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [query, setQuery] = useState("");
  const reload = () => setTick((t) => t + 1);

  const entries = useMemo(() => {
    // `tick` is the reload trigger — re-reads localStorage after each mutation.
    void tick;
    return listPartnerStorageEntries()
      .map((e) => ({ ...e, meta: describeKey(e.key) }))
      .sort((a, b) => a.meta.label.localeCompare(b.meta.label));
  }, [tick]);

  // Search filters the list of cached entries by name, description, key, or
  // category (the JSON viewer inside each row has its own separate search).
  const filteredEntries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      [e.meta.label, e.meta.description, e.key, CATEGORY[e.meta.category]].some(
        (s) => String(s || "").toLowerCase().includes(q),
      ),
    );
  }, [entries, query]);

  const grouped = useMemo(() => {
    const g = {};
    for (const e of filteredEntries) (g[e.meta.category] ||= []).push(e);
    return g;
  }, [filteredEntries]);

  const totalBytes = useMemo(
    () => entries.reduce((sum, e) => sum + e.sizeBytes, 0),
    [entries],
  );

  async function handleRefresh(entry) {
    const r = entry.meta.refresh;
    if (!r) return;
    setBusyKey(entry.fullKey);
    try {
      if (r.kind === "docTypes") {
        await getLoanFileDocGroupsAndTypes({ force: true });
      } else if (r.kind === "modelSchema") {
        await getModelSelectOptions({ objectType: r.objectType, force: true });
      }
      enqueueSnackbar(`Refreshed ${entry.meta.label}`, { variant: "success" });
    } catch {
      enqueueSnackbar(`Could not refresh ${entry.meta.label}`, { variant: "error" });
    } finally {
      setBusyKey(null);
      reload();
    }
  }

  function handleDelete(entry) {
    localStorage.removeItem(entry.fullKey);
    enqueueSnackbar(`Deleted ${entry.meta.label}`, { variant: "success" });
    reload();
  }

  function handleClearAll() {
    const count = clearAllPartnerStorage();
    setConfirmClear(false);
    enqueueSnackbar(
      count ? `Cleared ${count} cached item${count === 1 ? "" : "s"}` : "Nothing to clear",
      { variant: "success" },
    );
    reload();
  }

  return (
    <Box
      sx={{
        background: T.card,
        border: `1px solid ${T.line}`,
        borderRadius: T.radius,
        boxShadow: T.shadow,
        p: "20px 22px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
        <Box>
          <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink900 }}>Cached data</Box>
          <Box sx={{ fontSize: 12.5, color: T.ink500, mt: "3px" }}>
            Selections and preferences this portal saves in your browser
            {entries.length
              ? ` — ${entries.length} item${entries.length === 1 ? "" : "s"}, ${formatBytes(totalBytes)}.`
              : "."}
          </Box>
        </Box>
        <Box sx={{ ml: "auto" }}>
          <Button
            variant="outlined"
            startIcon={<DeleteSweepOutlined />}
            onClick={() => setConfirmClear(true)}
            disabled={!entries.length}
            sx={{
              color: T.ink700,
              borderColor: T.line,
              fontWeight: 600,
              borderRadius: "8px",
              whiteSpace: "nowrap",
              "&:hover": { borderColor: T.ink300, background: "#f8fafb" },
            }}
          >
            Clear all
          </Button>
        </Box>
      </Box>

      {entries.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <SearchInput
            value={query}
            onChange={setQuery}
            fullWidth
            size="small"
            placeholder="Search cached data"
            sx={{ maxWidth: 360 }}
          />
        </Box>
      )}

      {entries.length === 0 ? (
        <Box sx={{ fontSize: 13, color: T.ink500, py: 1 }}>
          No cached data is currently stored.
        </Box>
      ) : filteredEntries.length === 0 ? (
        <Box sx={{ fontSize: 13, color: T.ink500, py: 1 }}>
          No cached data matches “{query}”.
        </Box>
      ) : (
        CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
          <Box key={cat} sx={{ mb: 3.5, "&:last-of-type": { mb: 0 } }}>
            <Box
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".04em",
                textTransform: "uppercase",
                color: T.ink300,
                mb: "6px",
              }}
            >
              {CATEGORY[cat]}
            </Box>
            {grouped[cat].map((entry) => (
              <CacheRow
                key={entry.fullKey}
                entry={entry}
                busy={busyKey === entry.fullKey}
                onRefresh={handleRefresh}
                onDelete={handleDelete}
              />
            ))}
          </Box>
        ))
      )}

      <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Clear all cached data?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13.5 }}>
            This removes all {entries.length} saved item{entries.length === 1 ? "" : "s"} —
            selections, pipeline layouts and filters, table columns, and UI
            preferences. Reference data reloads automatically; your saved views
            and filters will reset to defaults. You will stay signed in.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: "12px 20px" }}>
          <Button variant="text" onClick={() => setConfirmClear(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleClearAll}
            sx={{ background: T.red, color: "#fff", "&:hover": { background: "#b91c1c" } }}
          >
            Clear all
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
